import React, { useState, useEffect, useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
} from "recharts";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const lines = content.split("\n");

  return (
    <div className="font-sans text-[#7E8081] space-y-2 text-[15px] leading-[19.5px]">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1"></div>;

        if (trimmed.startsWith("### "))
          return (
            <h3
              key={index}
              className="text-[16px] font-bold text-[#323334] mt-2 mb-1"
            >
              {parseInline(trimmed.slice(4))}
            </h3>
          );
        if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
          return (
            <div key={index} className="flex items-start ml-2 mb-1">
              <span className="mr-2 text-[#59AD31] text-lg leading-none">•</span>
              <span>{parseInline(trimmed.slice(2))}</span>
            </div>
          );
        }

        return (
          <p key={index} className="mb-2">
            {parseInline(trimmed)}
          </p>
        );
      })}
    </div>
  );
};

const LoadingAnalysisHelper = () => (
  <div className="flex items-center gap-2 text-[#7E8081] text-sm md:text-base animate-pulse">
    <svg className="animate-spin h-5 w-5 text-[#59AD31]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
    </svg>
    <span className="font-bold">Generating analysis...</span>
  </div>
);

function extractAnalysisText(resData, fallbackKeys = []) {
  if (!resData || typeof resData !== "object") {
    return "Could not load analysis at this time.";
  }

  if (typeof resData.analysis === "string" && resData.analysis.trim()) {
    return resData.analysis;
  }

  for (const key of fallbackKeys) {
    if (typeof resData[key] === "string" && resData[key].trim()) {
      return resData[key];
    }
  }

  return "Could not load analysis at this time.";
}

// etiquetas eje Y
const yAxisLabelHa = "1000 ha";
const yAxisDxHa = -yAxisLabelHa.length;

// ───────── helper para ordenar series por valor máximo ─────────
function getGroupedOrderByMaxValue(data, groups) {
  if (!data || data.length === 0) return groups.flat();

  // ordenamos los grupos según el valor máximo del PRIMER key del grupo
  const sortedGroups = [...groups].sort((a, b) => {
    const keyA = a[0];
    const keyB = b[0];

    const maxA = Math.max(
      ...data.map((d) => (typeof d[keyA] === "number" ? d[keyA] : 0))
    );
    const maxB = Math.max(
      ...data.map((d) => (typeof d[keyB] === "number" ? d[keyB] : 0))
    );

    return maxB - maxA; // descendente
  });

  // convertimos [[a,b],[c,d]] → [a,b,c,d]
  return sortedGroups.flat();
}

// Grupos de claves para la lógica de ordenamiento
const landUseGroups = [
  ["otherLand"],
  ["pasture"],
  ["cropland"],
  ["urban"],
  ["forest"],
];

// ───────── tooltip custom (ordenado por valor) ─────────
function CustomTooltip({ active, payload, label, chartType = "bar" }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => {
        const isTarget = item.dataKey === "targetShare";
        let icon;

        if (isTarget) {
          icon = (
            <svg width="16" height="3" viewBox="0 0 16 3" className="inline">
              <line x1="0" y1="1.5" x2="16" y2="1.5" stroke="#FF0000" strokeWidth="2" strokeDasharray="3,2" />
            </svg>
          );
        } else if (chartType === "line") {
          icon = (
            <svg width="16" height="2" viewBox="0 0 16 2" className="inline">
              <line x1="0" y1="1" x2="16" y2="1" stroke={item.color} strokeWidth="2" />
            </svg>
          );
        } else {
          icon = (
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
          );
        }

        return (
          <div
            key={item.dataKey}
            className="mb-0.5 flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-1.5">
              {icon}
              <span className="text-[#7E8081]">{item.name}</span>
            </div>
            <span className="font-medium text-[#323334]">
              {typeof item.value === "number"
                ? item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
                : item.value}{" "}
              <span className="text-[#98999A]">{yAxisLabelHa}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ───────── leyenda custom ordenada y clicable ─────────
function OrderedLegend({ payload, order, hiddenKeys, onToggle, chartType = "bar" }) {
  if (!payload) return null;

  const ordered = [...payload].sort(
    (a, b) => order.indexOf(a.dataKey) - order.indexOf(b.dataKey)
  );

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {ordered.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);

        return (
          <button
            key={entry.dataKey}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""
              }`}
          >
            {chartType === "line" ? (
              <svg width="20" height="2" viewBox="0 0 20 2" className="inline">
                <line x1="0" y1="1" x2="20" y2="1" stroke={entry.color || "#9999CC"} strokeWidth="2" />
              </svg>
            ) : (
              <span
                className="h-[3px] w-4 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
            )}

            <span className="text-[12px]">{entry.value}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function LandUseTab({ data, pathway, pathwayId, country, onLoadingChange }) {
  const [hiddenLandKeys, setHiddenLandKeys] = useState([]);
  const [landChartMode, setLandChartMode] = useState("line");

  // 🔄 Limpiar estados cuando cambian los datos
  useEffect(() => {
    setHiddenLandKeys([]);
  }, [data]);

  // Extract specific dataset
  const chartData = data ? data["Area by landcover"] : [];

  // States for LLM Analysis
  const [landAnalysis, setLandAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    onLoadingChange?.(loadingAnalysis);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAnalysis]);

  // ───────────────────── Data Fetching (LLM) ─────────────────────
  const fetchAnalysis = async (forceRegenerate = false) => {
    if (!country) {
      setLandAnalysis("");
      setLoadingAnalysis(false);
      return;
    }

    setLoadingAnalysis(true);
    try {
      // Prepare the data context for the LLM
      const payload = {
        analysisKey: "land_use_analysis",
        scope: "country",
        country,
        landUseData: {
          data: JSON.stringify(chartData),
          title: `Area by land cover - ${country}`,
        },
        context: "land",
        pathway,
        forceRegenerate,
        payload: {
          meta: {
            pathway_id: pathwayId,
            country,
          },
        },
      };

      const url = `${process.env.REACT_APP_API_URL}/api/chat/fablegpt`;

      const response = await fetch(
        url,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`Analysis API error: ${response.status}`);
      }

      const resData = await response.json();

      // Map API response key to state
      const next = resData.landUseAnalysis?.replace(/_/g, " ");
      setLandAnalysis(next);
    } catch (error) {
      console.error("Error fetching analysis:", error);
      setLandAnalysis("Cannot load analysis at this time.");
    } finally {
      setLoadingAnalysis(false);
    }
  };

  useEffect(() => {
    // Only fetch if data exists
    if (chartData && chartData.length > 0) {
      fetchAnalysis();
    }
    // eslint-disable-next-line
  }, [chartData, pathway, pathwayId]);

  // Calculate legend order dynamically based on the prop data
  const landLegendOrder = useMemo(
    () => getGroupedOrderByMaxValue(chartData, landUseGroups),
    [chartData]
  );

  const handleLegendClickLand = (o) => {
    const { dataKey } = o;
    if (!dataKey) return;
    setHiddenLandKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((k) => k !== dataKey)
        : [...prev, dataKey]
    );
  };

  return (
    <div className="flex w-full flex-col gap-10">
      {/* BLOQUE: Area by landcover */}
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <h2 className="text-[18px] font-medium text-[#323334]">
            Area by land cover
          </h2>
          <div className="flex flex-wrap items-center gap-2 mt-2 sm:mt-0">
            <div className="flex rounded-full border border-[#D9DCDC] bg-white p-1 shadow-sm">
              {[
                { value: "line", label: "Line" },
                { value: "area", label: "Area" },
                { value: "bar", label: "Bar" },
              ].map((mode) => {
                const active = landChartMode === mode.value;
                return (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setLandChartMode(mode.value)}
                    className={`rounded-full px-3 py-1 text-sm transition ${active ? "bg-[#59AD31] text-white" : "text-[#7E8081] hover:bg-[#F4F7F6]"}`}
                  >
                    {mode.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#1E5B4F] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#C49A1F]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download image"
              onClick={async () => {
                const chartSection = document.getElementById('landuse-chart-section');
                if (!chartSection) return;
                const description = 'Area by land cover';
                const html2canvas = (await import('html2canvas')).default;
                html2canvas(chartSection, { scale: 2, useCORS: true }).then((canvas) => {
                  canvas.toBlob((blob) => {
                    if (!blob) return;
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${description.replace(/\s+/g, '_')}.jpg`;
                    link.click();
                    URL.revokeObjectURL(url);
                  }, 'image/jpeg', 0.95);
                });
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de imagen */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="8.5" cy="10.5" r="1.5" fill="currentColor" />
                  <path d="M21 19l-5.5-7-4.5 6-3-4-4 5" stroke="currentColor" strokeWidth="2" fill="none" />
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download image</span>
            </button>
            <button
              type="button"
              className="group flex items-center p-0 w-10 h-10 rounded-full bg-gradient-to-tr from-[#59AD31] to-[#C49A1F] text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 overflow-hidden hover:w-44 text-center focus:outline-none focus:ring-2 focus:ring-[#59AD31]"
              style={{ transitionProperty: 'width,background,color,box-shadow,transform', minWidth: '2.5rem', maxWidth: '11rem' }}
              aria-label="Download data"
              onClick={async () => {
                const description = 'Area by land cover';
                const Papa = (await import('papaparse')).default;
                if (!chartData || !Array.isArray(chartData) || chartData.length === 0) return;
                const headers = Object.keys(chartData[0]);
                const csvRows = [
                  [`Description: ${description}`],
                  [],
                  headers,
                  ...chartData.map(row => headers.map(h => row[h]))
                ];
                const csv = Papa.unparse(csvRows, { quotes: true, skipEmptyLines: true });
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${description.replace(/\s+/g, '_')}.csv`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <span className="relative w-10 h-10 transition-all duration-200">
                {/* Icono de archivo clásico */}
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:opacity-0 absolute inset-0 m-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path d="M6 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8.828A2 2 0 0 0 19.414 7.414l-4.828-4.828A2 2 0 0 0 13.172 2H6z" stroke="currentColor" strokeWidth="2"/>
                  <path d="M14 2v6a2 2 0 0 0 2 2h6" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </span>
              <span className="max-w-0 opacity-0 group-hover:max-w-[160px] group-hover:opacity-100 transition-all duration-200 whitespace-nowrap text-sm">Download data</span>
            </button>
          </div>
        </div>

        <div id="landuse-chart-section" className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            {landChartMode === "bar" ? (
              <BarChart
                data={chartData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label value="Years" position="insideBottom" offset={-20} style={{ fill: "#98999A", fontSize: 15 }} />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) => Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })}
                >
                  <Label value={yAxisLabelHa} angle={-90} position="left" dx={yAxisDxHa} style={{ fill: "#98999A", fontSize: 15 }} />
                </YAxis>

                <Tooltip content={(props) => <CustomTooltip {...props} chartType="bar" />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={landLegendOrder}
                      hiddenKeys={hiddenLandKeys}
                      onToggle={handleLegendClickLand}
                      chartType="bar"
                    />
                  )}
                />

                <Bar dataKey="cropland" name="Cropland" fill="#80D5FF" stackId="landuse" hide={hiddenLandKeys.includes("cropland")} />
                <Bar dataKey="otherLand" name="Otherland" fill="#FF5579" stackId="landuse" hide={hiddenLandKeys.includes("otherLand")} />
                <Bar dataKey="pasture" name="Pasture" fill="#FB752E" stackId="landuse" hide={hiddenLandKeys.includes("pasture")} />
                <Bar dataKey="forest" name="Forest" fill="#2FCD76" stackId="landuse" hide={hiddenLandKeys.includes("forest")} />
                <Bar dataKey="urban" name="Urban" fill="#9999CC" stackId="landuse" hide={hiddenLandKeys.includes("urban")} />
              </BarChart>
            ) : (
              <ComposedChart
                data={chartData}
                margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
              >
                <CartesianGrid strokeDasharray="3 3" />

                <XAxis
                  dataKey="year"
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={70}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickMargin={10}
                >
                  <Label value="Years" position="insideBottom" offset={-20} style={{ fill: "#98999A", fontSize: 15 }} />
                </XAxis>

                <YAxis
                  width={60}
                  tick={{ fill: "#98999A", fontSize: 12 }}
                  tickFormatter={(v) => Number(v).toLocaleString("en-US", { maximumFractionDigits: 1 })}
                >
                  <Label value={yAxisLabelHa} angle={-90} position="left" dx={yAxisDxHa} style={{ fill: "#98999A", fontSize: 15 }} />
                </YAxis>

                <Tooltip
                  content={(props) => <CustomTooltip {...props} chartType={landChartMode} />}
                  cursor={{ stroke: "rgba(0,0,0,0.15)", strokeWidth: 1 }}
                />

                <Legend
                  verticalAlign="top"
                  align="left"
                  wrapperStyle={{ top: 0, left: 0 }}
                  content={(props) => (
                    <OrderedLegend
                      {...props}
                      order={landLegendOrder}
                      hiddenKeys={hiddenLandKeys}
                      onToggle={handleLegendClickLand}
                      chartType={landChartMode}
                    />
                  )}
                />

                {landChartMode === "line" ? (
                  <>
                    <Line type="monotone" dataKey="cropland" name="Cropland" stroke="#80D5FF" strokeWidth={2} dot={false} hide={hiddenLandKeys.includes("cropland")} />
                    <Line type="monotone" dataKey="otherLand" name="Otherland" stroke="#FF5579" strokeWidth={2} dot={false} hide={hiddenLandKeys.includes("otherLand")} />
                    <Line type="monotone" dataKey="pasture" name="Pasture" stroke="#FB752E" strokeWidth={2} dot={false} hide={hiddenLandKeys.includes("pasture")} />
                    <Line type="monotone" dataKey="forest" name="Forest" stroke="#2FCD76" strokeWidth={2} dot={false} hide={hiddenLandKeys.includes("forest")} />
                    <Line type="monotone" dataKey="urban" name="Urban" stroke="#9999CC" strokeWidth={2} dot={false} hide={hiddenLandKeys.includes("urban")} />
                  </>
                ) : (
                  <>
                    <Area type="monotone" dataKey="cropland" name="Cropland" stroke="#80D5FF" fill="#80D5FF" fillOpacity={0.25} dot={false} stackId="landuse" hide={hiddenLandKeys.includes("cropland")} />
                    <Area type="monotone" dataKey="otherLand" name="Otherland" stroke="#FF5579" fill="#FF5579" fillOpacity={0.25} dot={false} stackId="landuse" hide={hiddenLandKeys.includes("otherLand")} />
                    <Area type="monotone" dataKey="pasture" name="Pasture" stroke="#FB752E" fill="#FB752E" fillOpacity={0.25} dot={false} stackId="landuse" hide={hiddenLandKeys.includes("pasture")} />
                    <Area type="monotone" dataKey="forest" name="Forest" stroke="#2FCD76" fill="#2FCD76" fillOpacity={0.25} dot={false} stackId="landuse" hide={hiddenLandKeys.includes("forest")} />
                    <Area type="monotone" dataKey="urban" name="Urban" stroke="#9999CC" fill="#9999CC" fillOpacity={0.25} dot={false} stackId="landuse" hide={hiddenLandKeys.includes("urban")} />
                  </>
                )}
              </ComposedChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Dynamic Analysis Text */}
        <div className="w-full min-h-[100px]">
          {loadingAnalysis ? (
            <div className="flex items-center gap-2 text-[#7E8081] text-sm animate-pulse">
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Generating analysis...
            </div>
          ) : (
            <div className="group relative">
              <button
                onClick={() => fetchAnalysis(true)}
                title="Regenerate analysis"
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 p-2 text-[#98999A] hover:text-[#59AD31] transition-all bg-white rounded-full shadow-sm border border-gray-100"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div id="desc-chart_area_by_land_cover"><NativeAnalysis content={landAnalysis} /></div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}